/**
 * Libraries a CODE step can import in batch mode (PIPE-10, PIPE-12, PIPE-13).
 *
 * The list is fixed: the logic-sandbox image installs exactly these versions,
 * pinned by wheel hash (backend/logic_sandbox/requirements-udf.txt). pandas
 * and numpy load with every batch; the rest only when the code imports them,
 * and then the batch gets at least `memoryMb` of memory
 * (udf_exec.EXTRA_LIBRARIES). test_udf_libraries.py checks these versions and
 * floors against the backend.
 */
export interface CodeLibrary {
  /** What the code imports. */
  module: string;
  /** How people call it. */
  label: string;
  version: string;
  /** Least memory a batch gets when the code imports it. */
  memoryMb: number;
  /** Loaded with every batch (pandas, numpy). */
  always?: boolean;
  /** 'catalog': not in the sandbox image; installed on first use from the
   *  Workbench catalog (hash-pinned wheels) and mounted read-only (PIPE-13). */
  source?: 'image' | 'catalog';
}

export const CODE_LIBRARIES: CodeLibrary[] = [
  { module: 'pandas', label: 'pandas', version: '2.3.3', memoryMb: 192, always: true },
  { module: 'numpy', label: 'numpy', version: '2.5.3', memoryMb: 192, always: true },
  { module: 'scipy', label: 'scipy', version: '1.18.1', memoryMb: 384 },
  { module: 'sklearn', label: 'scikit-learn', version: '1.9.1', memoryMb: 512 },
  { module: 'statsmodels', label: 'statsmodels', version: '0.15.0', memoryMb: 512 },
  { module: 'rapidfuzz', label: 'rapidfuzz', version: '3.14.6', memoryMb: 192 },
  { module: 'dateutil', label: 'python-dateutil', version: '2.9.0.post0', memoryMb: 192 },
  { module: 'pytz', label: 'pytz', version: '2026.5', memoryMb: 192 },
];

/**
 * Libraries a pipeline declares from the catalog (PIPE-13): the Workbench's
 * DS-3 wheels (backend/kernel_service/env_catalog), installed by
 * kernel-env-builder and mounted read-only in the sandbox. The same table as
 * backend/logic_sandbox/udf_catalog.json (test_udf_catalog.py checks it).
 */
export const CATALOG_LIBRARIES: CodeLibrary[] = [
  { module: 'networkx', label: 'networkx', version: '3.4.2', memoryMb: 192, source: 'catalog' },
  { module: 'sympy', label: 'sympy', version: '1.13.3', memoryMb: 256, source: 'catalog' },
  { module: 'holidays', label: 'holidays', version: '0.105', memoryMb: 192, source: 'catalog' },
  { module: 'phonenumbers', label: 'phonenumbers', version: '9.0.40', memoryMb: 192, source: 'catalog' },
  { module: 'openpyxl', label: 'openpyxl', version: '3.1.5', memoryMb: 192, source: 'catalog' },
  { module: 'tabulate', label: 'tabulate', version: '0.9.0', memoryMb: 192, source: 'catalog' },
];

/** Three or more catalog libraries loaded together need more than the
 *  largest of their own floors (udf_catalog.COMBINED_FLOOR_MB). */
export const CATALOG_COMBINED_FLOOR_MB = 256;
export const CATALOG_COMBINED_AFTER = 3;

/** A step that names n_jobs or joblib gets this much more address space for
 *  the sandbox's worker threads (PIPE-15, main.py UDF_THREADS_EXTRA_MB). */
export const THREADS_EXTRA_MB = 32;
const PARALLEL_WORDS = /\b(n_jobs|joblib|Parallel|parallel_backend|parallel_config)\b/;

/** Whether the code asks for parallel work (n_jobs, joblib). */
export const usesParallel = (code: string): boolean => PARALLEL_WORDS.test(code || '');

/** What a step may declare in `config.libraries`: the image's extra libraries
 *  and the catalog. pandas and numpy are always loaded. */
export const DECLARABLE_LIBRARIES: CodeLibrary[] = [...CODE_LIBRARIES.filter(l => !l.always), ...CATALOG_LIBRARIES];

export const MAX_DECLARED_LIBRARIES = 8;

const BY_MODULE = new Map([...CODE_LIBRARIES, ...CATALOG_LIBRARIES].map(l => [l.module, l]));

/** The step's declared libraries: known ids only, in catalog order. */
export function declaredLibraries(value: unknown): string[] {
  const ids = Array.isArray(value) ? value.map(v => String(v)) : [];
  return DECLARABLE_LIBRARIES.filter(l => ids.includes(l.module)).map(l => l.module);
}

/** Add or remove a library from the declaration. */
export function toggleLibrary(declared: string[], module: string): string[] {
  if (declared.includes(module)) return declared.filter(m => m !== module);
  if (declared.length >= MAX_DECLARED_LIBRARIES) return declared;
  return declaredLibraries([...declared, module]);
}

/** The libraries of the list the code imports, in list order. Reads
 *  `import a.b as c, d` and `from a.b import c` anywhere in the code (inside a
 *  function too), the same statements the sandbox reads. */
export function importedLibraries(code: string): CodeLibrary[] {
  const tops = new Set<string>();
  // importlib.import_module("scipy.stats") / __import__("x") with a literal
  // name: the sandbox loads those before the guard too (PIPE-13).
  for (const m of (code || '').matchAll(/\b(?:import_module|__import__)\(\s*['"]([A-Za-z_][\w.]*)['"]/g)) tops.add(m[1].split('.')[0]);
  for (const line of (code || '').split('\n')) {
    const from = /^\s*from\s+([A-Za-z_][\w.]*)\s+import\b/.exec(line);
    if (from) { tops.add(from[1].split('.')[0]); continue; }
    const imp = /^\s*import\s+(.+)$/.exec(line);
    if (imp) {
      for (const part of imp[1].split('#')[0].split(',')) {
        const name = part.trim().split(/\s+/)[0];
        if (name) tops.add(name.split('.')[0]);
      }
    }
  }
  return [...CODE_LIBRARIES, ...CATALOG_LIBRARIES].filter(l => tops.has(l.module));
}

/** Least memory (MB) a batch of this code gets for the extra libraries it
 *  imports or declares, or 0 when only pandas and numpy (or nothing). A
 *  catalog library counts only when declared: undeclared it does not load. */
export function librariesMemoryMb(code: string, declared: string[] = []): number {
  const used = importedLibraries(code).filter(l => !l.always && l.source !== 'catalog');
  const decl = declared.map(m => BY_MODULE.get(m)).filter((l): l is CodeLibrary => !!l);
  const floors = [...used, ...decl].map(l => l.memoryMb);
  if (decl.filter(l => l.source === 'catalog').length >= CATALOG_COMBINED_AFTER) floors.push(CATALOG_COMBINED_FLOOR_MB);
  return Math.max(0, ...floors);
}

/** Catalog libraries the code imports without declaring them: the sandbox
 *  refuses those, so the editor says where to declare them. */
export function undeclaredCatalogImports(code: string, declared: string[]): CodeLibrary[] {
  return importedLibraries(code).filter(l => l.source === 'catalog' && !declared.includes(l.module));
}

export const libraryByModule = (module: string): CodeLibrary | undefined => BY_MODULE.get(module);
